import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import SessionUserHeader from '../Component/SessionAndUserManagement/SessionUserHeader';
import SessionConfigTable from '../Component/SessionAndUserManagement/SessionConfigTable';
import UserConfigTable from '../Component/SessionAndUserManagement/UserConfigTable';
import SessionConfigPopup from '../Component/SessionAndUserManagement/SessionConfigPopup';
import UserConfigPopup from '../Component/SessionAndUserManagement/UserConfigPopup';
import ConfirmDeleteDialog from '../Component/SessionAndUserManagement/ConfirmDeleteDialog';
import EdiFileProcessManagement from './EdiFileProcessManagement';
import { fetchFixSessionConfig, fetchFixUserConfig, updateUserConfig, updateSessionConfig } from '../http/api';
import { useAuth } from '../Context/AuthProvider';
import { tokens } from '../Common/designTokens';
import { useSearchParams } from 'react-router-dom';
import useSnackbar from '../Utils/useSnackbar';
import { useSessionConfigStore } from '../Store/sessionConfigStore';
import { useUserConfigStore } from '../Store/userConfigStore';
import { useEdiFileProcessStore } from '../Store/ediFileProcessStore';
import useUserConfigWebSocketUpdate from '../Utils/useUserConfigWebSocketUpdate';
import useSessionConfigWebSocketUpdate from '../Utils/useSessionConfigWebSocketUpdate';
import { usePageSubscription } from '../Utils/WebSocketProvider.jsx';
import { canWriteSessionUserConfig } from '../Constants/AppConstants';

const SessionAndUserManagement = () => {
  const { token, role } = useAuth();
  usePageSubscription("sessionUserConfig");
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess, SnackbarComponent } = useSnackbar();
  const canWriteConfig = canWriteSessionUserConfig(role);
  const writeAccessDeniedMessage = 'Only SUPER_ADMIN can modify session and user configuration.';
  const showWriteAccessDenied = () => showError(writeAccessDeniedMessage);
  
  // Tab management - 0 for Session, 1 for User, 2 for EDI file processing
  const tabParam = searchParams.get('tab') || '0';
  const initialTab = parseInt(tabParam, 10);
  const [activeTab, setActiveTab] = useState(initialTab >= 0 && initialTab <= 2 ? initialTab : 0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Popup state management
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState('add'); // 'add' or 'edit'
  const [selectedRow, setSelectedRow] = useState(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('session'); // 'session' or 'user'
  const [deleteRowData, setDeleteRowData] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sort configs for each tab
  const [sortConfigs, setSortConfigs] = useState({
    '0': { key: 'id', direction: 'desc' },     // Session tab
    '1': { key: 'userId', direction: 'desc' },     // User tab
  });

  // Get current sort config based on active tab
  const getCurrentSortConfig = () => sortConfigs[activeTab.toString()] || { key: 'id', direction: 'desc' };

  // Store state
  const {
    sessionData,
    totalElements: sessionTotalElements,
    loading: sessionLoading,
    error: sessionError,
    setSessionData,
    setTotalElements: setSessionTotalElements,
    setLoading: setSessionLoading,
    setError: setSessionError,
  } = useSessionConfigStore();

  const {
    userData,
    totalElements: userTotalElements,
    loading: userLoading,
    error: userError,
    setUserData,
    setTotalElements: setUserTotalElements,
    setLoading: setUserLoading,
    setError: setUserError,
  } = useUserConfigStore();

  const { loading: ediFileProcessLoading } = useEdiFileProcessStore();

  useUserConfigWebSocketUpdate();
  useSessionConfigWebSocketUpdate();

  // Track visited tabs to avoid redundant API calls
  const [visitedTabs, setVisitedTabs] = useState(new Set([activeTab]));

  // Compute loading state based on active tab
  const loading = activeTab === 0 ? sessionLoading : activeTab === 1 ? userLoading : ediFileProcessLoading;

  // Effect to sync tab with URL
  useEffect(() => {
    const tabFromUrl = parseInt(searchParams.get('tab') || '0', 10);
    if (tabFromUrl >= 0 && tabFromUrl <= 2 && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Effect to reset pagination on tab change
  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  // Fetch Session Config
  const fetchSessionData = async () => {
    if (!token) return;
    setSessionLoading(true);
    setSessionError(null);
    try {
      const sortConfig = getCurrentSortConfig();
      const response = await fetchFixSessionConfig(
        token,
        page,
        rowsPerPage,
        sortConfig.key,
        sortConfig.direction.toUpperCase()
      );
      if (response?.success && response?.data) {
        setSessionData(response.data.content || []);
        setSessionTotalElements(response.data.totalElements || 0);
      } else {
        setSessionError('Failed to load session configuration data');
        setSessionData([]);
        setSessionTotalElements(0);
      }
    } catch (error) {
      console.error('Error fetching session config:', error);
      setSessionError(error.message || 'Failed to load session configuration data');
      setSessionData([]);
      setSessionTotalElements(0);
    } finally {
      setSessionLoading(false);
    }
  };

  // Fetch User Config
  const fetchUserData = async () => {
    if (!token) return;
    setUserLoading(true);
    setUserError(null);
    try {
      const sortConfig = getCurrentSortConfig();
      const response = await fetchFixUserConfig(
        token,
        page,
        rowsPerPage,
        sortConfig.key,
        sortConfig.direction.toUpperCase()
      );
      if (response?.success && response?.data) {
        setUserData(response.data.content || []);
        setUserTotalElements(response.data.totalElements || 0);
      } else {
        setUserError('Failed to load user configuration data');
        setUserData([]);
        setUserTotalElements(0);
      }
    } catch (error) {
      console.error('Error fetching user config:', error);
      setUserError(error.message || 'Failed to load user configuration data');
      setUserData([]);
      setUserTotalElements(0);
    } finally {
      setUserLoading(false);
    }
  };

  // Effect to fetch data when tab changes or pagination changes
  useEffect(() => {
    const shouldFetch = !visitedTabs.has(activeTab) || page > 0 || rowsPerPage !== 20;
    
    if (activeTab === 0) {
      if (shouldFetch || sessionData.length === 0) {
        fetchSessionData();
        setVisitedTabs(prev => new Set([...prev, activeTab]));
      }
    } else if (activeTab === 1) {
      if (shouldFetch || userData.length === 0) {
        fetchUserData();
        setVisitedTabs(prev => new Set([...prev, activeTab]));
      }
    }
  }, [activeTab, page, rowsPerPage]);

  // Separate effect for sort changes - reset to page 0 and fetch
  useEffect(() => {
    setPage(0);
    if (activeTab === 0) {
      fetchSessionData();
    } else if (activeTab === 1) {
      fetchUserData();
    }
  }, [sortConfigs]);

  // Handle tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab.toString() });
  };

  // Handle Add/Edit button click
  const handleAddEdit = () => {
    if (!canWriteConfig) {
      showWriteAccessDenied();
      return;
    }
    setPopupMode('add');
    setSelectedRow(null);
    setPopupOpen(true);
  };

  // Handle edit click from table row
  const handleEditClick = (rowData) => {
    if (!canWriteConfig) {
      showWriteAccessDenied();
      return;
    }
    setPopupMode('edit');
    setSelectedRow(rowData);
    setPopupOpen(true);
  };

  // Handle delete click from table row for User Config
  const handleDeleteUserClick = (rowData) => {
    if (!canWriteConfig) {
      showWriteAccessDenied();
      return;
    }
    setDeleteType('user');
    setDeleteRowData(rowData);
    setDeleteDialogOpen(true);
  };

  // Confirm delete user config
  const confirmDeleteUser = async (rowData) => {
    if (!canWriteConfig) {
      showWriteAccessDenied();
      return;
    }
    setIsDeleting(true);
    try {
      const payload = {
        venueId: rowData.venueId,
        userId: rowData.userId,
        venueUser: null,
        sessionUpdateAction: 'REMOVE',
      };
      const response = await updateUserConfig(token, payload);
      if (response?.success) {
        // Update is handled via WebSocket; no refetch needed
        setDeleteDialogOpen(false);
        setDeleteRowData(null);
        showSuccess('User configuration deleted successfully');
      } else {
        // Handle success: false case
        showError(response?.message || 'Failed to delete user configuration');
      }
    } catch (error) {
      console.error('Error deleting user config:', error);
      // Handle error case (e.g., 400, 500, etc.)
      showError(error?.message || 'Failed to delete user configuration');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete click from table row for Session Config
  const handleDeleteSessionClick = (rowData) => {
    if (!canWriteConfig) {
      showWriteAccessDenied();
      return;
    }
    setDeleteType('session');
    setDeleteRowData(rowData);
    setDeleteDialogOpen(true);
  };

  // Confirm delete session config
  const confirmDeleteSession = async (rowData) => {
    if (!canWriteConfig) {
      showWriteAccessDenied();
      return;
    }
    setIsDeleting(true);
    try {
      const payload = {
        sessionUpdateAction: 'REMOVE',
        custId: rowData.custId,
        venueId: rowData.venueId,
        senderCompId: rowData.senderCompId,
        targetCompId: rowData.targetCompId,
        connectionType: rowData.connectionType,
        groupMasterId: rowData.groupMasterId,
        groupName: rowData.groupName || '',
        customerName: rowData.customerName || '',
        venueName: rowData.venueName || '',
        targetHost: '',
        targetPort: null,
        startTime: '',
        endTime: '',
      };
      const response = await updateSessionConfig(token, payload);
      if (response?.success) {
        // Refetch session data after successful delete
        fetchSessionData();
        setDeleteDialogOpen(false);
        setDeleteRowData(null);
        showSuccess('Session configuration deleted successfully');
      } else {
        // Handle success: false case
        showError(response?.message || 'Failed to delete session configuration');
      }
    } catch (error) {
      console.error('Error deleting session config:', error);
      // Handle error case (e.g., 400, 500, etc.)
      showError(error?.message || 'Failed to delete session configuration');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle popup success - refresh data
  const handlePopupSuccess = () => {
    if (activeTab === 0) {
      fetchSessionData();
    }
  };

  const handlePopupSuccessWithMessage = () => {
    handlePopupSuccess();
  };

  // Handle popup close
  const handlePopupClose = () => {
    setPopupOpen(false);
    setSelectedRow(null);
  };

  // Handle popup submit success
  const handlePopupSubmitSuccess = (formData) => {
    console.log(`${popupMode === 'add' ? 'Added' : 'Updated'} config:`, formData);
    // TODO: Integrate with API when ready
    handlePopupClose();
  };

  // Handle sort
  const handleSort = (columnKey) => {
    const tabKey = activeTab.toString();
    const currentConfig = sortConfigs[tabKey];
    
    let newDirection = 'asc';
    if (currentConfig.key === columnKey) {
      newDirection = currentConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfigs({
      ...sortConfigs,
      [tabKey]: { key: columnKey, direction: newDirection }
    });
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens.colors.bodyBackgroundColor,
      }}
    >
      {/* Header with tabs and Add/Edit button */}
      <SessionUserHeader
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        handleAddEdit={handleAddEdit}
        loading={loading}
        canWrite={canWriteConfig}
      />

      {/* Table */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          padding: '16px',
        }}
      >
        {activeTab === 0 ? (
          <SessionConfigTable
            data={sessionData}
            setData={setSessionData}
            sortConfig={getCurrentSortConfig()}
            onSort={handleSort}
            error={sessionError}
            loading={sessionLoading}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteSessionClick}
            canWrite={canWriteConfig}
            page={page}
            rowsPerPage={rowsPerPage}
            totalElements={sessionTotalElements}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        ) : activeTab === 1 ? (
          <UserConfigTable
            data={userData}
            setData={setUserData}
            sortConfig={getCurrentSortConfig()}
            onSort={handleSort}
            error={userError}
            loading={userLoading}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteUserClick}
            canWrite={canWriteConfig}
            page={page}
            rowsPerPage={rowsPerPage}
            totalElements={userTotalElements}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        ) : (
          <EdiFileProcessManagement embedded />
        )}
      </Box>

      {/* Popups */}
      {activeTab === 0 && (
        <SessionConfigPopup
          open={popupOpen}
          onClose={handlePopupClose}
          mode={popupMode}
          initialData={selectedRow}
          onSuccess={handlePopupSuccessWithMessage}
          showSuccess={showSuccess}
          showError={showError}
          canWrite={canWriteConfig}
        />
      )}

      {activeTab === 1 && (
        <UserConfigPopup
          open={popupOpen}
          onClose={handlePopupClose}
          mode={popupMode}
          initialData={selectedRow}
          onSuccess={handlePopupSuccessWithMessage}
          showSuccess={showSuccess}
          showError={showError}
          canWrite={canWriteConfig}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteRowData(null);
        }}
        onConfirm={deleteType === 'session' ? confirmDeleteSession : confirmDeleteUser}
        type={deleteType}
        rowData={deleteRowData}
        isLoading={isDeleting}
        canWrite={canWriteConfig}
      />

      {/* Snackbar for error/success notifications */}
      <SnackbarComponent />
    </Box>
  );
};

export default SessionAndUserManagement;
